import React, { useContext, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
  makeStyles,
  Theme,
  createStyles,
  FormHelperText,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { EnergyConsultationFormModel } from '../types';
import FormSectionCard from './FormSectionCard';
import { FormState } from '../../../hooks/useFormState.types';
import {
  ConsultationType,
  EnergyConsultationDayAvailability,
} from '../../../__generated__/pge-types';
import { useTranslation } from '../../../hooks/useTranslation';
import mainColors from '../../../themes/main-colors';
import PhoneIcon from './icons/PhoneIcon';
import EmailIcon from './icons/EmailIcon';
import SiteVisitIcon from './icons/SiteVisitIcon';
import ConsultationScheduler from './ConsultationScheduler';
import SignInForSiteVisitModal from './SignInForSiteVisitModal';
import { featureFlagsContext } from '../../../providers/FeatureFlagsProvider';
import Backdrop from '../../backdrop/Backdrop';

interface Props {
  form: FormState<EnergyConsultationFormModel>;
  availableSiteVisitDays: EnergyConsultationDayAvailability[];
  isPublicForm?: boolean;
}

interface ConsultationOption {
  label: string;
  icon: React.ReactNode;
  value: ConsultationType;
  description: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: mainColors.white,
    },
    description: {
      fontSize: 14,
      marginBottom: theme.spacing(2.5),
    },
    optionCard: {
      position: 'relative',
      height: 104,
      border: `1px solid ${mainColors.lightGray}`,
      borderRadius: 6,
      backgroundColor: mainColors.white,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'center',
      transition: '0.2s ease',
      '&:hover': {
        borderColor: mainColors.shoreGreen,
      },
    },
    selectedOptionCard: {
      border: `2px solid ${mainColors.shoreGreen}`,
      backgroundColor: mainColors.navBarHoverBackground,
    },
    checkCircle: {
      position: 'absolute',
      top: 8,
      left: 8,
      width: 20,
      height: 20,
      borderRadius: '50%',
      backgroundColor: mainColors.shoreGreen,
      color: mainColors.white,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    optionLabel: {
      fontSize: 16,
    },
    selectedDescription: {
      fontSize: 14,
      color: mainColors.grey4,
      marginTop: theme.spacing(2),
    },
    divider: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    dateTimeTitle: {
      fontSize: 16,
      fontWeight: 700,
      marginBottom: theme.spacing(1.5),
    },
    signinToView: {
      color: mainColors.roseRed,
      fontSize: '0.75rem',
      padding: '0 10px 0 10px',
    },
  }),
);

const ConsultationTypeSection: React.FC<Props> = ({
  form,
  availableSiteVisitDays,
  isPublicForm = false,
}) => {
  const classes = useStyles();
  const { t, inlineRichT } = useTranslation();
  const [showSigninModal, setShowSigninModal] = useState(false);
  const { isEnergyConsultationSiteVisitEnabled, loading } = useContext(
    featureFlagsContext,
  );

  if (loading) {
    return <Backdrop forceOpen />;
  }

  const consultationOptions: ConsultationOption[] = [
    {
      label: t('SITE_VISIT'),
      icon: <SiteVisitIcon />,
      value: ConsultationType.SiteVisit,
      description: '',
    },
    {
      label: t('PHONE_CALL'),
      icon: <PhoneIcon />,
      value: ConsultationType.PhoneCall,
      description: t('ENERGY_CONSULTATION_BY_PHONE'),
    },
    //Hidden based on Biz requirement
    /* {
      label: t('EMAIL'),
      icon: <EmailIcon />,
      value: ConsultationType.Email,
      description: t('ENERGY_CONSULTATION_BY_EMAIL'),
    }, */
  ].filter(
    option =>
      option.value !== ConsultationType.SiteVisit ||
      isEnergyConsultationSiteVisitEnabled,
  );

  const selectedOption = consultationOptions.find(
    option => option.value === form.values.consultationType,
  );

  const handleConsultationTypeChange = (type: ConsultationType) => {
    //Show a info modal to inform the customer to sign in.
    if (isPublicForm && type === ConsultationType.SiteVisit) {
      setShowSigninModal(true);
      return;
    }

    void form.setValue('consultationType', type);

    if (type !== ConsultationType.SiteVisit) {
      void form.setValue('scheduleDate', '');
      void form.setValue('scheduleTime', '');
    }
  };

  return (
    <FormSectionCard title={t('CONSULTATION_TYPE')}>
      <Typography className={classes.description}>
        {t('ENERGY_CONSULTATION_TYPE_INFO')}
      </Typography>

      <Grid container spacing={2}>
        {consultationOptions.map(option => {
          const isSelected = form.values.consultationType === option.value;

          return (
            <Grid item xs={6} md={6} key={option.value}>
              <Box
                role="button"
                tabIndex={0}
                className={`${classes.optionCard} ${
                  isSelected ? classes.selectedOptionCard : ''
                }`}
                onClick={() => handleConsultationTypeChange(option.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    handleConsultationTypeChange(option.value);
                  }
                }}
              >
                {isSelected && (
                  <Box className={classes.checkCircle}>
                    <CheckIcon style={{ fontSize: 16 }} />
                  </Box>
                )}

                {option.icon}

                <Typography className={classes.optionLabel}>
                  {option.label}
                </Typography>
              </Box>
            </Grid>
          );
        })}
        {form.errors.consultationType && (
          <FormHelperText error>{form.errors.consultationType}</FormHelperText>
        )}
      </Grid>

      {selectedOption &&
        form.values.consultationType !== ConsultationType.SiteVisit && (
          <Typography className={classes.selectedDescription}>
            {selectedOption.description}
          </Typography>
        )}

      {!isPublicForm &&
        form.values.consultationType === ConsultationType.SiteVisit && (
          <>
            <Divider className={classes.divider} />

            <ConsultationScheduler
              date={form.values.scheduleDate || null}
              onDateChange={e => form.setValue('scheduleDate', e)}
              time={form.values.scheduleTime}
              onTimeChange={time => form.setValue('scheduleTime', time)}
              preferredLanguage={form.values.preferredLanguage}
              availableDays={availableSiteVisitDays}
              postalCode={form.values.postalCode || ''}
            />

            {form.errors.scheduleDate && (
              <FormHelperText error>{form.errors.scheduleDate}</FormHelperText>
            )}

            {form.errors.scheduleTime && (
              <FormHelperText error>{form.errors.scheduleTime}</FormHelperText>
            )}
          </>
        )}

      {showSigninModal && (
        <SignInForSiteVisitModal
          open={true}
          onClose={() => setShowSigninModal(false)}
        />
      )}
    </FormSectionCard>
  );
};

export default ConsultationTypeSection;
